import React, { useState, useRef, useEffect } from "react";
import { useHotkeys } from "react-hotkeys-hook";
import { toast } from "react-hot-toast";
import API from "../api";
import { capture } from "../instrument";
import { LuX } from "react-icons/lu";

const LaboratoryLookup = ({ onChange, initialValues = [], placeholder = "Sélectionner des laboratoires" }) => {
  const [inputValue, setInputValue] = useState("");
  const [selectedLaboratories, setSelectedLaboratories] = useState([]);
  const [allLaboratories, setAllLaboratories] = useState([]);
  const [filteredLaboratories, setFilteredLaboratories] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [isLoading, setIsLoading] = useState(false);
  const inputRef = useRef(null);
  const containerRef = useRef(null);
  const dropdownRef = useRef(null);
  const itemsRef = useRef([]);
  const hasInitializedRef = useRef(false);

  useEffect(() => {
    const fetchLaboratories = async () => {
      try {
        setIsLoading(true);
        const response = await API.get("/admin/laboratory");
        if (!response.ok) {
          toast.error(response.message || "Erreur lors du chargement des laboratoires");
          return;
        }
        setAllLaboratories(response.data);
      } catch (error) {
        capture(error);
        toast.error("Une erreur est survenue");
      } finally {
        setIsLoading(false);
      }
    };

    fetchLaboratories();
  }, []);

  useEffect(() => {
    if (allLaboratories.length === 0 || hasInitializedRef.current === true) return;
    hasInitializedRef.current = true;
    const initialLabs = allLaboratories.filter((lab) => initialValues.includes(lab._id));
    setSelectedLaboratories(initialLabs);
  }, [initialValues, allLaboratories]);

  useEffect(() => {
    const selectedIds = selectedLaboratories.map((lab) => lab._id);
    const available = allLaboratories.filter((lab) => !selectedIds.includes(lab._id));

    let filtered;
    if (inputValue.trim() === "") {
      filtered = available;
    } else {
      const searchTerm = inputValue.toLowerCase();
      filtered = available.filter((lab) => lab.name.toLowerCase().includes(searchTerm));
    }

    setFilteredLaboratories(filtered);
    setSelectedIndex(filtered.length > 0 ? 0 : -1);
  }, [inputValue, allLaboratories, selectedLaboratories]);

  useEffect(() => {
    if (selectedIndex >= 0 && itemsRef.current[selectedIndex]) {
      itemsRef.current[selectedIndex].scrollIntoView({ block: "nearest" });
    }
  }, [selectedIndex]);

  useEffect(() => {
    itemsRef.current = itemsRef.current.slice(0, filteredLaboratories.length);
  }, [filteredLaboratories]);

  const addLaboratory = (laboratory) => {
    if (selectedLaboratories.find((lab) => lab._id === laboratory._id)) {
      toast.error("Ce laboratoire est déjà sélectionné");
      return;
    }

    const newSelected = [...selectedLaboratories, laboratory];
    setSelectedLaboratories(newSelected);
    if (onChange) onChange(newSelected.map((lab) => lab._id));

    setInputValue("");
    setSelectedIndex(-1);
    if (inputRef.current) inputRef.current.focus();
  };

  const removeLaboratory = (laboratoryToRemove) => {
    const newSelected = selectedLaboratories.filter((lab) => lab._id !== laboratoryToRemove._id);
    setSelectedLaboratories(newSelected);
    if (onChange) onChange(newSelected.map((lab) => lab._id));
    if (inputRef.current) inputRef.current.focus();
  };

  const navOpts = { enabled: showDropdown === true && filteredLaboratories.length > 0, enableOnFormTags: ["INPUT"], preventDefault: true };
  useHotkeys("down", () => setSelectedIndex((i) => Math.min(i + 1, filteredLaboratories.length - 1)), navOpts, [showDropdown, filteredLaboratories]);
  useHotkeys("up", () => setSelectedIndex((i) => Math.max(i - 1, 0)), navOpts, [showDropdown, filteredLaboratories]);
  useHotkeys("enter", () => addLaboratory(filteredLaboratories[selectedIndex >= 0 ? selectedIndex : 0]), navOpts, [showDropdown, filteredLaboratories, selectedIndex]);

  const handleKeyDown = (e) => {
    if (e.key === "Escape") {
      setShowDropdown(false);
      setSelectedIndex(-1);
      if (inputRef.current) inputRef.current.blur();
    } else if (e.key === "Backspace" && inputValue === "" && selectedLaboratories.length > 0) {
      removeLaboratory(selectedLaboratories[selectedLaboratories.length - 1]);
    }
  };

  const handleContainerClick = () => {
    if (inputRef.current) inputRef.current.focus();
  };

  return (
    <div className="relative w-full">
      <div
        ref={containerRef}
        className="bg-white flex flex-wrap items-center gap-1 p-2 border rounded-lg transition-all duration-200 min-h-[42px] cursor-text"
        onClick={handleContainerClick}
      >
        {selectedLaboratories.map((lab) => (
          <div key={lab._id} className="bg-primary-100 text-primary-800 px-1 py-0.5 rounded-md text-sm flex items-center">
            {lab.name}
            <button
              className="ml-1 text-primary-600 hover:text-primary-800"
              onClick={(e) => {
                e.stopPropagation();
                removeLaboratory(lab);
              }}
              type="button"
              aria-label={`Supprimer ${lab.name}`}
            >
              <LuX className="h-4 w-4" />
            </button>
          </div>
        ))}
        <div className="flex items-center flex-grow">
          <input
            ref={inputRef}
            className="flex-grow outline-none min-w-[120px]"
            placeholder={selectedLaboratories.length === 0 ? placeholder : ""}
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyDown={handleKeyDown}
            onFocus={() => setShowDropdown(true)}
            onBlur={() =>
              setTimeout(() => {
                setShowDropdown(false);
                setSelectedIndex(-1);
              }, 200)
            }
          />
          {isLoading && <div className="animate-spin h-4 w-4 border-2 border-primary-500 border-t-transparent rounded-full mr-2"></div>}
        </div>
      </div>

      {showDropdown && filteredLaboratories.length > 0 && (
        <div ref={dropdownRef} className="absolute z-10 w-full bg-white border border-gray-200 rounded-lg shadow-lg overflow-auto max-h-48">
          {filteredLaboratories.map((lab, idx) => (
            <div
              ref={(el) => (itemsRef.current[idx] = el)}
              key={lab._id}
              className={`p-3 cursor-pointer flex justify-between items-center border-b border-gray-100 last:border-b-0 ${
                selectedIndex === idx ? "bg-primary-50 text-primary-700" : "hover:bg-gray-50"
              }`}
              onMouseDown={() => addLaboratory(lab)}
            >
              <span className="font-medium">{lab.name}</span>
              {selectedIndex === idx && <div className="text-primary-500 bg-primary-100 rounded px-2 py-1 text-xs font-medium">Enter</div>}
            </div>
          ))}
        </div>
      )}

      {showDropdown && filteredLaboratories.length === 0 && !isLoading && (
        <div className="absolute z-10 w-full bg-white border border-gray-200 rounded-lg shadow-lg p-3 text-gray-500 text-sm">
          {allLaboratories.length === 0 ? "Aucun laboratoire disponible" : "Aucun laboratoire trouvé"}
        </div>
      )}
    </div>
  );
};

export default LaboratoryLookup;
